import { ApolloLink, Operation } from '@apollo/client';
import { v4 as uuid4 } from 'uuid';

const correlationIdHeader = 'x-correlation-id';

export interface FailedRequest {
  correlationId?: string;
  operationName?: string;
  occurredAt: string;
}

// eslint-disable-next-line @typescript-eslint/ban-types
type ErrorKey = object;

// Keyed on the error objects Apollo hands to onError. It puts those same objects on the ApolloError the caller catches,
// so an error shown on screen finds its own request, not whichever request happened to fail last.
const failedRequests = new WeakMap<ErrorKey, FailedRequest>();

// Tags every API request with its own id. Kong keeps an incoming x-correlation-id and ezpay-api-svc logs under it,
// so the id shown on an error screen finds the matching request in the API logs.
export const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: { ...headers, [correlationIdHeader]: uuid4() },
  }));
  return forward(operation);
});

export const recordFailedRequest = (operation: Operation, errorKeys: (ErrorKey | null | undefined)[]): void => {
  const failedRequest: FailedRequest = {
    correlationId: operation.getContext().headers?.[correlationIdHeader],
    operationName: operation.operationName,
    occurredAt: new Date().toISOString(),
  };
  errorKeys.forEach(key => {
    if (key) failedRequests.set(key, failedRequest);
  });
};

interface ApolloErrorShape {
  networkError?: ErrorKey | null;
  graphQLErrors?: readonly ErrorKey[];
}

export const getFailedRequest = (error: unknown): FailedRequest | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const { networkError, graphQLErrors } = error as ApolloErrorShape;
  return (
    (networkError ? failedRequests.get(networkError) : undefined) ?? (graphQLErrors ? failedRequests.get(graphQLErrors) : undefined)
  );
};
